import { Box, Typography } from '@mui/material';
import { isValid } from 'date-fns';
import React from 'react';
import { DateTimePicker } from '../DateTimePicker';
/**
 * A dual date-time picker component allowing users to select a date and time range.
 *
 * @component
 *
 * @param {DateTimeRangePickerProps} props - Props for the DateTimeRangePicker component.
 * @param {SxProps} [props.sx] - Optional custom styling using the `sx` prop.
 * @param {string} props.fromDateLabel - Label for the "from" date field.
 * @param {string} props.fromTimeLabel - Label for the "from" time field.
 * @param {string} props.toDateLabel - Label for the "to" date field.
 * @param {string} props.toTimeLabel - Label for the "to" time field.
 * @param {string} [props.separatorLabel] - Optional text shown between the two pickers.
 * @param {Date} [props.minDate] - Minimum date allowed for selection.
 * @param {Date} [props.maxDate] - Maximum date allowed for selection.
 * @param {'24h' | '12h'} [props.timeFormat] - Time format to display, defaults to `'24h'`.
 * @param {1 | 5 | 10 | 15 | 30 | 60} [props.timeMinuteStep] - Time step for selection, default is 30.
 * @param {boolean} [props.required] - Whether selection is required for both pickers.
 * @param {boolean} [props.disabled] - Whether the pickers are disabled.
 * @param {boolean} [props.readOnly] - Whether the pickers are read-only.
 * @param {boolean} [props.fullWidth] - Whether the component should expand to fill the container width.
 * @param {'small' | 'medium'} [props.size] - Size of the pickers, default is `'medium'`.
 * @param {boolean} [props.error] - Whether to display an error state.
 * @param {number} [props.fromDateTabIndex] - Tab index for the "from" date picker.
 * @param {number} [props.fromTimeTabIndex] - Tab index for the "from" time picker.
 * @param {number} [props.toDateTabIndex] - Tab index for the "to" date picker.
 * @param {number} [props.toTimeTabIndex] - Tab index for the "to" time picker.
 * @param {DateTimeRangePickerValue} props.value - The selected from/to date range value.
 * @param {(newValue: DateTimeRangePickerValue) => void} props.onChange - Callback triggered when either picker value changes.
 * @param {boolean} [props.autoFocus] - Whether to autofocus the first field.
 * @param {string} [props.ariaDescribedBy] - ARIA ID for accessibility description.
 *
 * @returns {React.ReactElement} A rendered DateTimeRangePicker component.
 */
export const DateTimeRangePicker = (props) => {
    const fromValue = getDateTimeValue(props.value.fromDate, props.value.fromValue);
    const toValue = getDateTimeValue(props.value.toDate, props.value.toValue);
    const handleFromChange = (newFromValue) => {
        props.onChange({
            fromDate: newFromValue.date,
            toDate: toValue.date,
            fromValue: newFromValue,
            toValue: toValue,
        });
    };
    const handleToChange = (newToValue) => {
        props.onChange({
            fromDate: fromValue.date,
            toDate: newToValue.date,
            fromValue: fromValue,
            toValue: newToValue,
        });
    };
    const rootSx = {
        display: 'inline-flex',
        gap: props.separatorLabel ? 1 : 2,
        flexWrap: 'wrap',
        justifyContent: {
            xs: 'center',
            sm: 'flex-start',
        },
    };
    const childSx = {
        flexBasis: { xs: '100%', sm: 0 },
        flexGrow: 1,
        maxWidth: {
            xs: '100%',
            sm: props.fullWidth ? '100%' : 536,
        },
        flexDirection: {
            xs: 'column',
            sm: 'row',
        },
    };
    if (props.fullWidth) {
        rootSx.width = '100%';
    }
    return (React.createElement(Box, { sx: Object.assign(Object.assign({}, rootSx), props.sx), "data-test": 'date-time-range-picker' },
        React.createElement(DateTimePicker, { sx: childSx, dateLabel: props.fromDateLabel, timeLabel: props.fromTimeLabel, minDate: props.minDate, maxDate: pickSmallerDate(toValue.date, props.maxDate), timeFormat: props.timeFormat, timeMinuteStep: props.timeMinuteStep, required: props.required, disabled: props.disabled, readOnly: props.readOnly, fullWidth: props.fullWidth, size: props.size, error: props.error, dateTabIndex: props.fromDateTabIndex, timeTabIndex: props.fromTimeTabIndex, value: fromValue, onChange: handleFromChange, "data-test-date": 'from-date-picker', "data-test-time": 'from-time-picker', autoFocus: props.autoFocus, ariaDescribedBy: props.ariaDescribedBy }),
        props.separatorLabel && (React.createElement(Typography, { component: 'div', sx: {
                my: 2,
                mx: {
                    xs: 0,
                    sm: 1,
                },
            } }, props.separatorLabel)),
        React.createElement(DateTimePicker, { sx: childSx, dateLabel: props.toDateLabel, timeLabel: props.toTimeLabel, minDate: pickLargerDate(fromValue.date, props.minDate), maxDate: props.maxDate, timeFormat: props.timeFormat, timeMinuteStep: props.timeMinuteStep, required: props.required, disabled: props.disabled, readOnly: props.readOnly, fullWidth: props.fullWidth, size: props.size, error: props.error, dateTabIndex: props.toDateTabIndex, timeTabIndex: props.toTimeTabIndex, value: toValue, onChange: handleToChange, "data-test-date": 'to-date-picker', "data-test-time": 'to-time-picker', ariaDescribedBy: props.ariaDescribedBy })));
};
function getDateTimeValue(date, value) {
    if (value) {
        return value;
    }
    else if (date) {
        if (isValid(date)) {
            return { date };
        }
        else {
            return { dateValue: { invalidDate: date } };
        }
    }
    else {
        return {};
    }
}
function pickSmallerDate(a, b) {
    if (a && b) {
        return a.getTime() < b.getTime() ? a : b;
    }
    else if (a) {
        return a;
    }
    else if (b) {
        return b;
    }
    else {
        return undefined;
    }
}
function pickLargerDate(a, b) {
    if (a && b) {
        return a.getTime() > b.getTime() ? a : b;
    }
    else if (a) {
        return a;
    }
    else if (b) {
        return b;
    }
    else {
        return undefined;
    }
}
